import { RootState } from 'redux/store';

export type TimeFormatPreference = '12h' | '24h' | 'auto';

// The user's date/time-format preference, persisted in
// users.user_settings.timeFormat. Anything unrecognised (incl. absent) is
// treated as 'auto' — the pre-preference behaviour (follow the browser locale).
export const selectTimeFormat = (state: RootState): TimeFormatPreference => {
  const tf = state.auth.userData?.user_settings?.timeFormat;
  return tf === '12h' || tf === '24h' || tf === 'auto' ? tf : 'auto';
};

// Resolves the preference into an Intl `hour12` value. `undefined` means
// "do not force a cycle — let Intl resolve it from the browser locale".
export const selectHour12Option = (state: RootState): boolean | undefined => {
  const tf = selectTimeFormat(state);
  if (tf === '12h') return true;
  if (tf === '24h') return false;
  return undefined;
};

// Whether timestamps render in the browser's local timezone (true) or UTC /
// their source timezone (false). Persisted in user_settings.useLocalTimezone.
export const selectUseLocalTimezone = (state: RootState): boolean => state.auth.userData?.user_settings?.useLocalTimezone ?? false;

// The languages the UI ships translations for (src/locales/<code>.json).
export const SUPPORTED_LANGUAGES = ['en', 'nl'] as const;
export type AppLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const isSupported = (lng: unknown): lng is AppLanguage => SUPPORTED_LANGUAGES.some((code) => code === lng);

// The language the UI renders in. `user_settings.appLanguage` is what the
// language switcher writes; `users.language` is a server-side column that is
// currently never populated, kept here as a fallback in case it becomes
// authoritative. Anything unrecognised (incl. absent) resolves to 'en', which
// is also the `lng` src/i18n.ts initialises with.
//
// This is the single source of truth for the applied language — read it,
// never `i18n.language`, so the selector stays the only thing that decides.
export const selectAppLanguage = (state: RootState): AppLanguage => {
  const { language, user_settings } = state.auth.userData || {};
  if (isSupported(user_settings?.appLanguage)) return user_settings.appLanguage;
  if (isSupported(language)) return language;
  return 'en';
};
